import axios from '../api/axios';

/**
 * Drip campaign entries.
 *
 * The lead statuses come from the BACK END rather than a copy in this file, so the list the form
 * offers and the list the server validates against can never drift apart.
 */

export type DripActionType = 'interval' | 'trigger';
export type DripTriggerEvent = 'lead_received' | 'lead_status_changed';
export type DripStatus = 'draft' | 'active' | 'paused';

export interface DripEntry {
  id: string;
  name: string;
  actionType: DripActionType;
  status: DripStatus;
  subject: string;
  /** Inbox preview line. Falls back to the subject when blank. */
  preheader?: string | null;
  body: string;
  sendGridTemplateId?: string | null;
  inactiveDays?: number;
  statuses?: string[];
  triggerEvent?: DripTriggerEvent;
  fromStatus?: string | null;
  toStatus?: string | null;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
  lastRunAt?: string | null;
  sentLast7Days?: number;
}

export interface LeadStatus {
  category: string;
  group: string;
  status: string;
  /** target = may be emailed · stop = a person is engaged · never = never email · ask = unconfirmed */
  drip: 'target' | 'stop' | 'never' | 'ask';
}

export interface LeadStatusCatalog {
  source?: string;
  note?: string;
  statuses: LeadStatus[];
}

/** The server's message when it refuses a save - it names the actual problem, so show it as-is. */
export function dripErrorMessage(err: any, fallback: string): string {
  return err?.response?.data?.error || err?.response?.data?.message || fallback;
}

export interface DripSettings {
  /** The template every entry inherits unless it names its own. */
  defaultTemplateId: string | null;
  unsubscribeGroupId: number | null;
  defaultRepName: string;
  blockers: string[];
}

export async function getDripSettings(): Promise<DripSettings> {
  const { data } = await axios.get('/drip/settings');
  return data;
}

export async function getLeadStatuses(): Promise<LeadStatusCatalog> {
  const { data } = await axios.get('/drip/lead-statuses');
  return data;
}

export async function getDripEntries(): Promise<DripEntry[]> {
  const { data } = await axios.get('/drip/campaigns');
  return data.entries ?? [];
}

export async function createDripEntry(entry: Partial<DripEntry>): Promise<DripEntry> {
  const { data } = await axios.post('/drip/campaigns', entry);
  return data;
}

export async function updateDripEntry(id: string, entry: Partial<DripEntry>): Promise<DripEntry> {
  const { data } = await axios.put(`/drip/campaigns/${id}`, entry);
  return data;
}

export async function deleteDripEntry(id: string): Promise<void> {
  await axios.delete(`/drip/campaigns/${id}`);
}

export interface DripPreview {
  templateId: string | null;
  templateData: { subject: string; preheader: string; body: string };
  /** Merge fields the renderer could not fill in, by name - a misspelled token shows up here. */
  unresolvedTokens: string[];
  sampleFirstName: string;
  /** The SendGrid unsubscribe group every send belongs to. Null means sending is not possible. */
  unsubscribeGroupId?: number | null;
  /** The signature used when a lead has no assigned advisor. */
  signatureFallback?: string;
  /** Settings that would stop a real send today, in plain words. */
  blockers?: string[];
}

/** Render an entry the way SendGrid will receive it. Uses a sample name, never a real lead. */
export async function previewDripEntry(entry: Partial<DripEntry>): Promise<DripPreview> {
  const { data } = await axios.post('/drip/preview', { entry });
  return data;
}

export interface TestSendResult {
  ok: boolean;
  to: string;
  statusCode: number;
  messageId?: string | null;
  unresolvedTokens: string[];
  sentInLastHour: number;
  limitPerHour: number;
}

/**
 * Send one drip email to a member of staff, through the real SendGrid path.
 *
 * The server accepts @lauruscollege.edu addresses only, one at a time, capped per hour, and writes
 * an audit record. No lead or drip state is touched.
 */
export async function sendDripTest(entry: Partial<DripEntry>, toEmail: string,
                                   repName?: string): Promise<TestSendResult> {
  const { data } = await axios.post('/drip/test-send', { entry, toEmail, repName });
  return data;
}

export interface SeedResult {
  created: string[];
  skipped: string[];
  /** Entries this seed had created earlier and has now replaced (refresh only). */
  replaced?: string[];
  rejected: { name: string; reason: string }[];
}

/**
 * Create the approved EAB cadence as drafts. Idempotent: existing entries are left alone.
 *
 * `refresh` replaces the entries a previous run created - draft ones only, and only those - so a
 * wording change reaches them. Anything active, or written by a person, is never touched.
 */
export async function seedEabCadence(refresh = false): Promise<SeedResult> {
  const { data } = await axios.post('/drip/seed-eab-cadence', { refresh });
  return data;
}

// ---------------------------------------------------------------------------------------------
// Uploading a file of leads
//
// EAB's leads arrive as email to the Admissions distribution list before Verity has them, so the
// team already holds the names. Uploading that list directly is what lets the sequence start
// without waiting on a Verity integration.
// ---------------------------------------------------------------------------------------------

export interface ImportRow {
  row: number | null;
  email: string;
  firstName?: string;
  leadId: string;
  existingStatus?: string;
  /** For email and PDF: the line the address was found on, so a person can check it. */
  context?: string;
}

export interface ImportProblem {
  row: number;
  email?: string;
  error: string;
}

export interface ImportResult {
  committed: boolean;
  /** Preview only */
  wouldImport?: number;
  wouldUpdate?: number;
  newLeads?: ImportRow[];
  existingLeads?: ImportRow[];
  message?: string;
  /** After committing */
  imported?: number;
  updated?: number;
  failed?: number;
  problems: ImportProblem[];
  /** 'pdf' | 'email' when the leads were extracted rather than read from columns. */
  extractedFrom?: string | null;
  /** How many of the extracted rows carried a LABELLED first name. */
  namesFound?: number;
}

/**
 * Send a lead file. `commit` false previews and writes nothing — always preview first, so a wrong
 * column is caught before anyone is emailed rather than after.
 */
export async function importDripLeads(csv: string, commit = false): Promise<ImportResult> {
  const { data } = await axios.post('/drip/leads/import', { csv, commit });
  return data;
}

/**
 * Send an Excel workbook. A .xlsx is a zip archive rather than text, so it goes base64-encoded and
 * is flattened to CSV on the server — then through exactly the same parser a CSV takes.
 */
export async function importDripLeadsXlsx(xlsxBase64: string, commit = false): Promise<ImportResult> {
  const { data } = await axios.post('/drip/leads/import', { xlsxBase64, commit });
  return data;
}

/**
 * Send a PDF or a saved email (.eml). Leads are extracted from the text; a first name is only taken
 * where the document labels it, so a row may legitimately arrive with no name rather than a guessed
 * one. A .eml with a PDF attached is read through to the attachment.
 */
export async function importDripLeadsFile(
  kind: 'pdf' | 'eml', base64: string, commit = false,
): Promise<ImportResult> {
  const body = kind === 'pdf' ? { pdfBase64: base64, commit } : { emlBase64: base64, commit };
  const { data } = await axios.post('/drip/leads/import', body);
  return data;
}

// ---------------------------------------------------------------------------------------------
// Leads
//
// The same people the EAB importer sends to Verity. They arrive automatically when EAB deposits a
// file, or from a file uploaded on the Drip Campaign tab.
// ---------------------------------------------------------------------------------------------

/**
 * A Verity check result held on a lead: kinds, times and run ids only. A status name never appears
 * here - only inside a stop reason.
 */
export interface DripVerityHold {
  kind?: string;
  since?: string;
  runId?: string;
  /** not-found only: the eab- ids Verity could not find. */
  notFoundIds?: string[];
  /** stop-pending-review only: the stop the breaker held back. */
  stopReason?: string;
  stopClass?: string;
}

export interface DripVerityCheck {
  /** allow | hold | stop | none */
  action?: string;
  kind?: string | null;
  since?: string;
  runId?: string;
}

/** Dry run only: what enforce WOULD have done. */
export interface DripVerityShadow extends DripVerityCheck {
  sendDate?: string | null;
  sendStep?: string | null;
}

export interface DripActionLogEntry {
  at?: string;
  by?: string;
  action?: string;
  reason?: string;
  runId?: string;
  prior?: Record<string, unknown>;
}

/**
 * Admissions welcomed this lead by hand, outside SIMS (the Outlook email they sent every new lead
 * until SIMS went live). SIMS never sends such a lead its own welcome: it starts at the next email,
 * counted from `date`.
 */
export interface DripWelcomedOutside {
  /** The Pacific date of the hand-sent welcome, YYYY-MM-DD. By default the day the lead arrived. */
  date?: string;
  markedAt?: string;
  by?: string;
}

export type DripLeadStatus = 'active' | 'held' | 'stopped' | 'completed' | 'merged' | 'not_started';

export interface DripLead {
  id: string;
  leadId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  source: string;                 // the campaign: 'eab' today, more later
  // `string` too: a status this build does not know must render as text, never throw.
  status: DripLeadStatus | string;
  arrivedAt?: string;
  sentSteps: string[];
  skippedSteps: string[];
  /** manual: a person sent it with Send email now (`by` is who). */
  sentHistory?: { step: string; name?: string; date: string; at?: string; manual?: boolean; by?: string }[];
  lastSentDate?: string;
  lastSentStep?: string;
  stoppedAt?: string;
  stoppedBy?: string;
  stoppedReason?: string;
  createdAt?: string;
  /** 'eab-queue' when the importer handed it over, 'upload' or 'manual' otherwise. */
  importedFrom?: string;
  sourceFile?: string;
  // No phone or postcode: this sends email, so they are contact details with no purpose here.
  city?: string;
  state?: string;
  modality?: string;
  degreeLevel?: string;
  programInterest?: string;
  additionalInterests?: string;
  startTimeframe?: string;
  workExperience?: string;
  credentials?: string;
  /** Computed server-side: what this lead is due next, or why nothing is. */
  nextStep?: string | null;
  nextStepReason?: string;
  // The Verity status check. Every field is optional: older records have none of them.
  verityHold?: DripVerityHold | null;
  verityCheck?: DripVerityCheck | null;
  verityShadow?: DripVerityShadow | null;
  stoppedRunId?: string | null;
  actionLog?: DripActionLogEntry[];
  notStartedAt?: string | null;
  /** The Pacific date of the Day-0 email: the sequence counts its days from here. */
  sequenceStartDate?: string | null;
  /** Set when Admissions welcomed the lead by hand: SIMS skips its welcome. */
  welcomedOutside?: DripWelcomedOutside | null;
  mergedInto?: string | null;
  verityExternalIds?: string[];
  retiredIds?: string[];
  /** EAB may have handed this lead to Verity twice. */
  possibleVerityDuplicate?: boolean;
  /** How many unmerged SIMS records share this address. Merge duplicates needs 2 or more. */
  personRecords?: number;
}

export interface DripLeadList {
  leads: DripLead[];
  count: number;
  /** Entries currently active. Empty means nothing is sending, whatever the leads say. */
  activeEntries: string[];
}

export async function getDripLeads(status?: string): Promise<DripLeadList> {
  const { data } = await axios.get('/drip/leads', { params: status ? { status } : {} });
  return data;
}

export async function stopDripLead(id: string, reason: string): Promise<{ lead: DripLead }> {
  const { data } = await axios.post(`/drip/leads/${id}/stop`, { reason });
  return data;
}

export interface DripLeadActionBody {
  action: 'resume' | 'release-hold' | 'merge-duplicates';
  /** 5-300 characters. Every action is audited. */
  reason: string;
  /** release-hold of a not-found hold: the ids a person confirmed were merged away in Verity. */
  retireIds?: string[];
}

/**
 * A person's audited action on one lead. 409 with a plain sentence in `error` when the lead's state
 * does not allow it - show it with dripErrorMessage.
 */
export async function dripLeadAction(id: string, body: DripLeadActionBody): Promise<{ lead: DripLead }> {
  const { data } = await axios.post(`/drip/leads/${id}/action`, body);
  return data;
}

// ---------------------------------------------------------------------------------------------
// Welcomed outside SIMS
//
// Until SIMS sends on its own, Admissions sends every new lead its first email by hand, and stops the
// moment SIMS goes live. A lead already welcomed that way is marked, so SIMS skips its own welcome
// and starts at the next email. One lead: Survey.Admin or Drip.Manager; the go-live bulk mark:
// Drip.Manager only, refused once SIMS has gone live. Every mark and undo is audited.
// ---------------------------------------------------------------------------------------------

/**
 * Mark one lead welcomed outside SIMS. `date` (YYYY-MM-DD, Pacific) is the day of the hand-sent
 * welcome; left out, the server uses the day the lead arrived. It cannot be before that or after
 * today (400). 409 with a plain sentence when the lead cannot be marked - SIMS has already emailed
 * it, or it is stopped, merged, finished or not started.
 */
export async function markWelcomedOutside(id: string, date?: string): Promise<{ lead: DripLead }> {
  const body = date ? { action: 'welcomed_outside', date } : { action: 'welcomed_outside' };
  const { data } = await axios.post(`/drip/leads/${id}/action`, body);
  return data;
}

/** Undo the mark: SIMS sends its own welcome first. Refused (409) once SIMS has emailed the lead. */
export async function clearWelcomedOutside(id: string): Promise<{ lead: DripLead }> {
  const { data } = await axios.post(`/drip/leads/${id}/action`, { action: 'clear_welcomed_outside' });
  return data;
}

/** Counts only - never a lead. */
export interface WelcomedOutsideBulkResult {
  preview: boolean;
  /** Preview: the server's time of the count. Send it back as arrivedBefore so the run marks what it showed. */
  asOf?: string;
  /**
   * Preview: why a confirmed run would be refused (409) - SIMS has already emailed a lead, or can send
   * right now. Null before go-live.
   */
  blocked?: string | null;
  /** Preview: the leads a confirmed run would mark. */
  wouldMark?: number;
  /** Confirmed: the leads marked by this run. */
  marked?: number;
  alreadyMarked: number;
  /**
   * The leads left alone, by reason: held, stopped, merged, notStarted, completed, otherStatus,
   * emailedBySims, noArrival, arrivesLater, pastStartWindow - and changedSince for one that changed
   * during the run.
   */
  skipped: Record<string, number>;
  failed?: number;
  /** Not reached before the server's time limit. Safe to run again. */
  remaining?: number;
  incomplete?: boolean;
}

/** How many leads "Mark waiting leads as welcomed outside SIMS" would mark. Writes nothing. */
export async function previewWelcomedOutsideBulk(): Promise<WelcomedOutsideBulkResult> {
  const { data } = await axios.post('/drip/leads/welcomed-outside', { preview: true });
  return data;
}

/**
 * Go-live morning, once, right before the emails are activated (Drip.Manager): mark every lead still
 * waiting for its first SIMS email (active, never emailed by SIMS, arrived by `arrivedBefore` - the
 * preview's asOf - and not marked yet), each dated the day it arrived. Held, stopped and other leads
 * are left alone and counted. Safe to run again that morning, before the emails are activated; once
 * SIMS has gone live the server refuses it (409).
 */
export async function markWaitingWelcomedOutside(arrivedBefore?: string): Promise<WelcomedOutsideBulkResult> {
  const body = arrivedBefore ? { confirm: true, arrivedBefore } : { confirm: true };
  const { data } = await axios.post('/drip/leads/welcomed-outside', body);
  return data;
}

// ---------------------------------------------------------------------------------------------
// Send email now
//
// SIMS's Verity login gets the leads back without their email addresses. Since 2026-10-27 the automatic
// runs then use the address on SIMS's record (utils/drip_verity.evaluate_automatic); Send email now
// still lets a Drip.Manager or Survey.Admin send one lead its next due email from SIMS at once, and in
// a dry run it is the only way anything is sent. SIMS checks the lead in Verity right then, with its
// sender's rules; the person's word stands in only for an email address, when Verity returned none at
// all. Every other rule still applies (Verity's status, the send window, one email a lead a day, the
// sequence order, the kill switch, Disarm), and every check and send is audited and capped per person
// per hour.
// ---------------------------------------------------------------------------------------------

export interface SendNowEntry {
  step: string;
  name?: string;
  day?: number | null;
  subject?: string;
  /** The Verity lead statuses an interval email is for; empty for the welcome. */
  statuses: string[];
  /** True when Verity returned the address too; false when the person confirms it. */
  addressConfirmed: boolean;
}

export interface SendNowPreview {
  sendable: boolean;
  /** Why it cannot be sent now, in plain words; null when it can. */
  why: string | null;
  /** The address the email would go to (send it back with the email), or null with no email. */
  address: string | null;
  /** The email that is next for this lead, or null when there is none to send. */
  entry: SendNowEntry | null;
}

/** SIMS checks the lead in Verity and says what Send email now would send, or why not. Sends nothing. */
export async function previewSendNow(id: string): Promise<SendNowPreview> {
  const { data } = await axios.post(`/drip/leads/${id}/send-now`, { preview: true });
  return data;
}

/**
 * Send the previewed email. `step` and `address` are the preview's: the server checks Verity again and
 * refuses (409) if either has changed since. 400 without a 5-300 character reason; 409 with a plain
 * sentence when the lead cannot be emailed now, a Verity read of it that failed included; 429 over an
 * hourly cap; 503 when SIMS could not sign in to Verity at all (or SendGrid is not configured); 502
 * when SendGrid refused or may not have sent, with `error` saying whether the email still counts as
 * sent.
 */
export async function sendNow(id: string, step: string, address: string, reason: string): Promise<{
  sent: boolean; entry: { step: string; name?: string; day?: number | null };
}> {
  const { data } = await axios.post(`/drip/leads/${id}/send-now`, { attest: true, reason, step, address });
  return data;
}

// ---------------------------------------------------------------------------------------------
// The Verity status check
//
// SIMS reads each EAB lead's status from Verity before emailing it. Modes: not armed (after deploy;
// no lead is read) -> dry run (everything evaluated and recorded, nobody emailed, no lead stopped)
// -> enforce. Arming is a Survey.Admin action; Disarm is either drip role. The server enforces every
// role whatever this screen shows.
// ---------------------------------------------------------------------------------------------

export type VeritySendingState = 'not-armed' | 'paused' | 'dry-run' | 'enforcing';
export type VerityMode = 'unarmed' | 'dry-run' | 'enforce';
export type VerityScope = 'dry-run' | 'enforce';

export interface VerityArm {
  scope?: VerityScope | string;
  by?: string;
  at?: string;
  reason?: string;
}

export interface VerityHistoryItem {
  /** run-check: a Survey.Admin pressed Run the nightly check now (no scope or reason; runId is the run). */
  action?: 'arm' | 'disarm' | 'run-check' | string;
  scope?: string | null;
  by?: string;
  at?: string;
  reason?: string;
  runId?: string;
}

export interface VeritySecretState {
  /** present | missing | forbidden | disabled | identity_unavailable | another error kind */
  state?: string;
  /** The first 8 characters of the secret version, never the value. */
  versionTag?: string | null;
}

export interface VerityLastHealth {
  at?: string;
  by?: string;
  ok?: boolean;
  credTag?: string | null;
  username?: VeritySecretState;
  password?: VeritySecretState;
  token?: string;
  list?: string;
}

export interface VerityCatalogue {
  checkedAt?: string;
  verityCount?: number;
  matched?: number;
  unmapped?: string[];
  categoryMismatch?: string[];
  missing?: string[];
  duplicateNames?: string[];
}

/** Counts only - never a lead, an address or a status name. In a dry run they are "would" counts. */
export interface VerityRunCounts {
  persons?: number;
  due?: number;
  evaluated?: number;
  notReached?: number;
  lookups?: number;
  allow?: number;
  sent?: number;
  sendFailed?: number;
  stop?: Record<string, number>;
  hold?: Record<string, number>;
  /**
   * hold['address-not-usable'] split by why (no_emails, no_match, bad, old): the codes add up to it.
   * Counts only: no lead carries a reason.
   */
  addressReasons?: Record<string, number>;
  /**
   * hold['unfilled-placeholder'] by which {{...}} placeholder the email would still have contained,
   * by name ('First Name': no first name on file; '{{ or }}': one with a brace missing). One count per
   * person and name, so a person whose email lacked two counts under each. Counts only: no lead carries it.
   */
  unfilledTokens?: Record<string, number>;
  cleared?: number;
  unknownStatus?: number;
  day0Starts?: number;
  /** First SIMS emails to leads Admissions had welcomed by hand (their welcome skipped). */
  welcomedOutsideStarts?: number;
  notStarted?: number;
  /** Allowed by Verity, but in a status the step's entry does not list: not sent. */
  notTargeted?: number;
  skippedSteps?: number;
  completed?: number;
  duplicates?: number;
  errors?: Record<string, number>;
  purged?: number;
}

export interface VerityRunResolution {
  action?: string;
  by?: string;
  at?: string;
  counts?: Record<string, number>;
}

export interface VerityRun {
  id: string;
  /** send (the hourly sender) | sweep (the nightly check) */
  kind?: 'send' | 'sweep' | string;
  mode?: string;
  /** running | ok | paused | aborted | budget */
  status?: string;
  pauseKind?: string | null;
  createdAt?: string;
  finishedAt?: string | null;
  counts?: VerityRunCounts | null;
  breaker?: { threshold?: number | null; newStops?: number; tripped?: boolean } | null;
  resolutions?: VerityRunResolution[];
  /** Still `running` more than 15 minutes after it started: the host ended it. */
  didNotFinish?: boolean;
  /** A nightly check: nightly (the 02:15 Pacific timer) or manual (Run the nightly check now). */
  trigger?: 'nightly' | 'manual' | string | null;
  /** A manual run: the Survey.Admin who started it. */
  by?: string | null;
}

export interface VerityCadence {
  required?: number;
  active?: number;
  ready?: boolean;
  missingDays?: number[];
  duplicateDays?: number[];
  notRenderable?: string[];
  /** The seven entries, in day order. */
  names?: string[];
  activatedBy?: string | null;
  activatedAt?: string | null;
}

export interface VerityArmChecks {
  healthFresh?: boolean;
  noUnmapped?: boolean;
  noDuplicateNames?: boolean;
  dryRunNightOk?: boolean;
}

export interface VerityStatus {
  sendingState?: VeritySendingState | string;
  /** Plain words: why sending is paused. */
  pauseReason?: string | null;
  /** SIMS_DISABLE_DRIP_SENDS. While on, nothing can be emailed. */
  killSwitch?: boolean;
  mode?: VerityMode | string;
  arm?: VerityArm | null;
  history?: VerityHistoryItem[];
  lastHealth?: VerityLastHealth | null;
  catalogue?: VerityCatalogue | null;
  latch?: { rejected?: boolean; at?: string | null } | null;
  runs?: VerityRun[];
  pendingReview?: { runId: string; leads: number }[];
  cadence?: VerityCadence | null;
  armChecks?: VerityArmChecks | null;
}

export interface VerityHealthResult {
  credential?: { username?: VeritySecretState; password?: VeritySecretState };
  /** ok | latched | credential_rejected | skipped | another error kind */
  token?: { state?: string; ms?: number | null; advice?: string | null };
  statusList?: {
    state?: string;
    count?: number;
    matched?: number | null;
    unmapped?: string[];
    categoryMismatch?: string[];
    missing?: string[];
    duplicateNames?: string[];
  };
  latch?: { rejected?: boolean; at?: string | null };
  checkedAt?: string;
  ok?: boolean;
}

/** stoppedBy on a stop the Verity check made. Only these can be resumed or undone. */
export const VERITY_SYSTEM_ACTOR = 'system:verity';

const HOLD_TEXT: Record<string, { short: string; long: string }> = {
  'stop-pending-review': {
    short: 'waiting for review',
    long: 'Waiting for review: the run would have stopped more leads than the safety limit allows.',
  },
  'duplicate-sims-record': {
    short: 'duplicate SIMS record',
    long: 'Two SIMS records share this address. Merge them so the person is emailed once.',
  },
  'possible-duplicate-verity-record': {
    short: 'possible Verity duplicate',
    long: 'EAB may have handed this lead to Verity twice. Check Verity, then release the hold.',
  },
  'not-found': { short: 'not found in Verity', long: 'Verity could not find this lead.' },
  malformed: { short: 'unreadable answer', long: "Verity's answer could not be read." },
  'unrecognised-status': {
    short: 'unknown status',
    long: 'Verity shows a status SIMS does not know, so the lead waits until the catalogue covers it.',
  },
  'address-not-usable': {
    short: 'address not usable',
    long: 'The email address is not on the Verity record, or Verity marks it bad or old.',
  },
  'no-verity-id': {
    short: 'no Verity id',
    long: 'No Verity id: the lead was not handed over by EAB, and drip emails EAB leads only.',
  },
  'too-many-ids': { short: 'too many ids', long: 'More than five Verity ids belong to this person.' },
  // Not Verity's answer, and never a hold on a lead: SIMS's own refusal, made again on each run (a dry
  // run notes it on the lead's verityShadow, as it notes everything it would do).
  'unfilled-placeholder': {
    short: 'an unfilled placeholder',
    long: 'The email would still contain a {{...}} placeholder SIMS could not fill, usually because the lead '
      + 'has no first name on file, so SIMS does not send it. Add the first name (or correct the email) and a '
      + 'later run sends it.',
  },
};

/** A hold kind in plain words. A kind this build does not know is shown as itself, never thrown on. */
export function verityHoldText(kind?: string | null): { short: string; long: string } {
  const known = kind ? HOLD_TEXT[kind] : undefined;
  if (known) return known;
  const raw = kind || 'unknown';
  return { short: raw, long: `On hold (${raw}).` };
}

// Why address-not-usable holds held, as a run counts them (VerityRunCounts.addressReasons: the codes of
// utils/verity_client SEND_REASONS). Counts only: no lead carries a code, so a lead's row shows the hold
// kind alone. Each finishes "address not usable: ...". A code this build does not know shows as itself.
const ADDRESS_REASON_TEXT: Record<string, string> = {
  no_emails: 'Verity returned no email address',
  no_match: "the address is not on the lead's Verity record",
  bad: 'Verity marks the address bad',
  old: 'Verity marks the address old',
};

/** A run's address-not-usable reason code in plain words, or null when there is none. */
export function verityAddressReasonText(code?: string | null): string | null {
  if (!code) return null;
  return ADDRESS_REASON_TEXT[code] || code;
}

/**
 * An unfilled placeholder by name (VerityRunCounts.unfilledTokens) in plain words, as the morning alert
 * says it (utils/drip_verity.unfilled_text): 'First Name' is a lead with no first name on file, and
 * '{{ or }}' (utils/drip_render.BROKEN) a placeholder typed with a brace missing.
 */
export function verityUnfilledText(name: string): string {
  if (name === 'First Name') return 'no first name on file';
  if (name === '{{ or }}') return 'a placeholder with a brace missing';
  return `an unfilled placeholder {{${name}}}`;
}

/** The class of a Verity stop: what kind of status it was, without the status name. */
export function verityStopClassText(cls?: string | null): string {
  if (cls === 'never') return 'a status that must never be emailed';
  if (cls === 'dne') return 'Do Not Email';
  if (cls === 'stop') return 'a status meaning a person is engaged';
  return cls ? `Verity (${cls})` : 'Verity';
}

/** Everything the Verity checks panel shows. Reads SIMS only: no Verity or Key Vault call. */
export async function getVerityStatus(): Promise<VerityStatus> {
  const { data } = await axios.get('/drip/verity/status');
  return data;
}

/**
 * Check Verity: both Key Vault secrets, the login (skipped while a rejected login is latched) and
 * the status list. Never reads a lead. Limited to 6 an hour per person (429 past that).
 */
export async function checkVerity(): Promise<VerityHealthResult> {
  const { data } = await axios.post('/drip/verity/check', {});
  return data;
}

export interface VerityControlBody {
  action: 'arm' | 'disarm';
  scope?: VerityScope;
  /** 'ARM DRY RUN' or 'ARM ENFORCE' */
  confirm?: string;
  reason: string;
}

/** Arm (Survey.Admin) or Disarm (either drip role). Unmet prerequisites answer 409 {unmet, messages}. */
export async function verityControl(body: VerityControlBody): Promise<{ arm: VerityArm | null; history: VerityHistoryItem[] }> {
  const { data } = await axios.post('/drip/verity/control', body);
  return data;
}

/** Counts and codes only - never a lead. In a dry run they are "would" counts. */
export interface VerityRunCheckResult {
  runId: string;
  /** ok | budget | paused | aborted */
  status?: string;
  pauseKind?: string | null;
  mode?: string;
  counts?: {
    persons?: number;
    lookups?: number;
    allow?: number;
    /** Of allow: on EAB's address, because Verity listed none. */
    vouched?: number;
    /** Would stop, by class: stop | never | dne (Do Not Email). */
    stop?: Record<string, number>;
    hold?: Record<string, number>;
    addressReasons?: Record<string, number>;
    errors?: Record<string, number>;
    notReached?: number;
  };
  /** It read at least one lead from Verity, none malformed. */
  provesReads?: boolean;
  /** It found at least one lead SIMS would email. */
  wouldEmail?: boolean;
  /**
   * The run's record was saved as finished. False: its last write failed, so the record still says
   * running (Last runs then shows Did not finish) and the gate, which reads saved records, cannot count it.
   */
  recordSaved?: boolean;
  /** This run is the dry-run night arming enforce needs: finished OK, provesReads and wouldEmail, record saved. */
  dryRunNightOk?: boolean;
}

/**
 * The wait for Run the nightly check now. The request lasts as long as the check (its wall is 180 s,
 * inside Azure's 230 s limit on a request), so it waits longer than either; the client sets no other
 * timeout. The token is fresh: the shared client asks for one on every request (api/axios.ts).
 */
export const RUN_CHECK_TIMEOUT_MS = 240_000;

/**
 * Run the nightly check now (Survey.Admin, armed for dry run only): the 02:15 Pacific check, at once,
 * as the same run. It sends no email and counts toward arming enforce exactly as the nightly one does.
 * 409 while a check is already running (the nightly one included) or when the checks are not armed for
 * dry run; 429 past 3 an hour, everyone together, with nextAllowedAt (a server time); 503 when it could
 * not start, nothing read.
 */
export async function runNightlyCheckNow(): Promise<VerityRunCheckResult> {
  const { data } = await axios.post('/drip/verity/run-check', {}, { timeout: RUN_CHECK_TIMEOUT_MS });
  return data;
}

export type VerityResolveAction =
  'preview-undo' | 'undo-stops' | 'preview-pending' | 'apply-pending' | 'release-pending';

export interface VerityResolveBody {
  action: VerityResolveAction;
  /** Must be the run id. */
  confirm: string;
  /** Required for the three actions that write. */
  reason?: string;
}

export interface VerityResolveResult {
  runId: string;
  action: VerityResolveAction;
  /** The previews */
  leads?: { id: string; leadId?: string }[];
  skipped?: { id: string; leadId?: string; reason?: string }[];
  /** The writes */
  counts?: { done?: number; changedSince?: number; skipped?: number; remaining?: number };
  /** Ran out of time part-way. Safe to run again. */
  incomplete?: boolean;
}

/** Act on one run: preview or undo its Verity stops, or preview, apply or release its review holds. */
export async function resolveVerityRun(runId: string, body: VerityResolveBody): Promise<VerityResolveResult> {
  const { data } = await axios.post(`/drip/verity/runs/${runId}/resolve`, body);
  return data;
}

export interface DripCadenceBody {
  action: 'activate' | 'pause';
  /** 'ACTIVATE 7 EMAILS', for activate */
  confirm?: string;
  reason: string;
}

/**
 * Switch the seven in-scope EAB emails on together, or pause them (Survey.Admin or Drip.Manager).
 * All or nothing; the nurture emails are never touched. Unmet conditions answer 409
 * {missingDays, duplicateDays, notRenderable, alreadyActive}.
 */
export async function dripCadence(body: DripCadenceBody): Promise<{ action: string; entries: string[] }> {
  const { data } = await axios.post('/drip/cadence', body);
  return data;
}

// ---------------------------------------------------------------------------------------------
// Website inquiries that never reached Verity
//
// Read from the website's lead table (laurusinquiry's) when the tab opens. SIMS stores none of it:
// no copy, no timer - the server builds the list on request and holds it in memory for two minutes.
// ---------------------------------------------------------------------------------------------

export interface WebsiteUnroutedPerson {
  firstName: string;
  lastName: string;
  email: string;
  /** On this list only. SIMS holds no phone numbers anywhere else, and must not start. */
  phone: string;
  program: string;
  /** The campus they chose; "Not chosen" when they left the placeholder. */
  location: string;
  /** Why Verity did not take them, from their latest attempt. */
  reason: string;
  /** How many times they sent the form. laurusinquiry's automatic retries of one send count once. */
  attempts: number;
  firstSubmittedAt: string;
  lastSubmittedAt: string;
  /** Filtered list only: why the submission looks like spam. */
  filterReason?: string;
}

export interface WebsiteUnroutedReport {
  generatedAt: string;
  windowDays: number;
  people: WebsiteUnroutedPerson[];
  /** Likely spam. Kept, not dropped, so a person can check the filter's judgement. */
  filtered: WebsiteUnroutedPerson[];
  counts: {
    people: number;
    filtered: number;
    byReason: Record<string, number>;
    filteredByReason: Record<string, number>;
  };
}

/**
 * `refresh` skips the server's two-minute cache, unless the cached list is under 15 seconds old
 * (so repeated clicks cannot keep the table scanned back to back). A read that fails or runs out of time answers
 * 503 with a plain sentence in `error` (show it with dripErrorMessage) - never a partial list.
 */
export async function getWebsiteUnrouted(refresh = false): Promise<WebsiteUnroutedReport> {
  const { data } = await axios.get('/drip/website-unrouted', { params: refresh ? { refresh: 1 } : {} });
  return data;
}
